import React, { useState, useEffect } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import { X, Plus, Trash2 } from 'lucide-react';
import { useToast } from './Toast';
import { ordersService } from '../services/firebaseService';

export const PaymentModal = ({ isOpen, onClose, order, onSuccess }) => {
  const toast = useToast();
  const [payments, setPayments] = useState([]);
  const [currentPaymentMethod, setCurrentPaymentMethod] = useState('');
  const [currentAmount, setCurrentAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [changeToGive, setChangeToGive] = useState(0); // Monnaie à rendre
  const [paymentMode, setPaymentMode] = useState('total'); // 'total' ou 'product'
  const [productPayments, setProductPayments] = useState({}); // { itemIndex: { method, paid } }
  const [amountReceivedByProduct, setAmountReceivedByProduct] = useState({}); // { itemIndex: montant reçu }
  const [amountReceivedTotal, setAmountReceivedTotal] = useState(''); // Montant reçu pour le total

  const isModifyingPayment = order?.payment_status === 'paid';
  const isPaymentLocked = isModifyingPayment && order?.payment_method === 'online';

  useEffect(() => {
    if (order && isOpen) {
      if (isModifyingPayment && !isPaymentLocked) {
        // Modifier un paiement existant
        setPayments([{
          method: order.payment_method || '',
          amount: parseFloat(order.total)
        }]);
      } else {
        // Nouveau paiement
        setPayments([]);
      }
      setCurrentPaymentMethod('');
      setCurrentAmount('');
      setChangeToGive(0);
      setPaymentMode('total');
      setProductPayments({});
      setAmountReceivedByProduct({});
      setAmountReceivedTotal('');
    }
  }, [order, isModifyingPayment, isPaymentLocked, isOpen]);

  // Calcul du total payé par produit (supporte la nouvelle structure avec payments[])
  const totalPaidByProduct = Object.values(productPayments).reduce((sum, p) => sum + (p.totalPaid || p.amount || 0), 0);
  const remainingByProduct = order ? parseFloat(order.total) - totalPaidByProduct : 0;

  // Fonction pour payer un produit (supporte paiement partiel/mixte)
  const handlePayProduct = (itemIndex, method) => {
    const item = order?.items?.[itemIndex];
    if (!item) return;

    const itemTotal = (item.unit_price || item.price || 0) * (item.quantity || 1);
    const received = parseFloat(amountReceivedByProduct[itemIndex]) || 0;
    const existing = productPayments[itemIndex];
    const alreadyPaid = existing?.totalPaid || 0;
    const remainingForItem = itemTotal - alreadyPaid;

    // CAS 1: Montant saisi > 0 ET < restant → Paiement PARTIEL
    if (received > 0 && received < remainingForItem) {
      const amountToRecord = received;
      const newTotalPaid = alreadyPaid + amountToRecord;
      const newRemaining = itemTotal - newTotalPaid;

      setProductPayments(prev => ({
        ...prev,
        [itemIndex]: {
          itemName: item.name,
          itemTotal,
          payments: [...(existing?.payments || []), { method, amount: amountToRecord }],
          totalPaid: newTotalPaid,
          remaining: newRemaining
        }
      }));

      // Reset le champ "Reçu"
      setAmountReceivedByProduct(prev => ({ ...prev, [itemIndex]: '' }));

      toast.info(
        `💵 ${amountToRecord.toFixed(2)}€ en ${method === 'espece' ? 'espèces' : method === 'cb' ? 'CB' : 'TR'}`,
        `Reste ${newRemaining.toFixed(2)}€ à payer`
      );
      return;
    }

    // CAS 2: Montant saisi >= restant OU pas de montant → Paiement COMPLET du restant
    const amountToRecord = remainingForItem;
    const changeToReturn = received > remainingForItem ? received - remainingForItem : 0;

    setProductPayments(prev => ({
      ...prev,
      [itemIndex]: {
        itemName: item.name,
        itemTotal,
        payments: [...(existing?.payments || []), { method, amount: amountToRecord }],
        totalPaid: itemTotal,
        remaining: 0
      }
    }));

    // Reset le champ "Reçu"
    setAmountReceivedByProduct(prev => ({ ...prev, [itemIndex]: '' }));

    // Si monnaie à rendre
    if (changeToReturn > 0) {
      toast.info('💰 Monnaie à rendre', `${changeToReturn.toFixed(2)}€`);
    }
  };

  // Supprimer paiement produit
  const handleRemoveProductPayment = (itemIndex) => {
    setProductPayments(prev => {
      const newPayments = { ...prev };
      delete newPayments[itemIndex];
      return newPayments;
    });
  };

  // Convertir paiements par produit en paiements groupés par méthode
  // Supporte la nouvelle structure avec payments[] ET l'ancienne structure
  const getPaymentsFromProducts = () => {
    const byMethod = {};
    Object.values(productPayments).forEach(p => {
      // Nouvelle structure avec payments[]
      if (p.payments && p.payments.length > 0) {
        p.payments.forEach(payment => {
          if (!byMethod[payment.method]) {
            byMethod[payment.method] = { method: payment.method, amount: 0, items: [] };
          }
          byMethod[payment.method].amount += payment.amount;
          if (!byMethod[payment.method].items.includes(p.itemName)) {
            byMethod[payment.method].items.push(p.itemName);
          }
        });
      } else if (p.method) {
        // Ancienne structure (rétrocompatibilité)
        if (!byMethod[p.method]) {
          byMethod[p.method] = { method: p.method, amount: 0, items: [] };
        }
        byMethod[p.method].amount += p.amount || 0;
        byMethod[p.method].items.push(p.itemName);
      }
    });
    return Object.values(byMethod);
  };

  const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);
  const remaining = order ? parseFloat(order.total) - totalPaid : 0;

  const handleAddPayment = () => {
    if (!currentPaymentMethod) {
      toast.warning('Mode de paiement requis', 'Sélectionnez un mode de paiement');
      return;
    }

    const amountGiven = parseFloat(currentAmount) || remaining;

    // Validation robuste du montant
    if (isNaN(amountGiven) || amountGiven <= 0) {
      toast.warning('Montant invalide', 'Le montant doit être un nombre positif');
      return;
    }

    if (amountGiven > 10000) {
      toast.warning('Montant trop élevé', 'Le montant maximum est de 10 000€');
      return;
    }

    // Si le montant donné est supérieur au restant (ex: client donne un billet)
    // On enregistre seulement le montant dû et on calcule la monnaie à rendre
    let amountToRecord = amountGiven;
    let change = 0;

    if (amountGiven > remaining) {
      // Client donne plus que le montant dû
      change = amountGiven - remaining;
      amountToRecord = remaining; // On enregistre seulement ce qui est dû
      setChangeToGive(prev => prev + change);
      toast.info('💰 Monnaie à rendre', `${change.toFixed(2)}€ à rendre au client`);
    }

    setPayments([...payments, {
      method: currentPaymentMethod,
      amount: amountToRecord,
      amountGiven: amountGiven, // Garder trace du montant donné
      change: change // Monnaie rendue pour ce paiement
    }]);

    setCurrentPaymentMethod('');
    setCurrentAmount('');
  };

  const handleRemovePayment = (index) => {
    const removedPayment = payments[index];
    // Si ce paiement avait de la monnaie à rendre, on la retire du total
    if (removedPayment.change) {
      setChangeToGive(prev => Math.max(0, prev - removedPayment.change));
    }
    setPayments(payments.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Mode paiement par produit
    if (paymentMode === 'product') {
      if (Object.keys(productPayments).length === 0) {
        toast.warning('Aucun paiement', 'Sélectionnez un mode de paiement pour chaque produit');
        return;
      }

      if (remainingByProduct > 0.01) {
        toast.warning('Paiement incomplet', `Il reste ${remainingByProduct.toFixed(2)}€ à payer`);
        return;
      }

      setLoading(true);

      try {
        const paymentsGrouped = getPaymentsFromProducts();

        const paymentData = {
          payment_method: paymentsGrouped.length === 1 ? paymentsGrouped[0].method : 'multi',
          payments: paymentsGrouped,
          product_payments: productPayments,
          amount_received: totalPaidByProduct,
          change_given: 0
        };

        // FIREBASE DIRECT (plus de backend Python)
        await ordersService.updatePayment(order.id, paymentData);

        toast.success('Paiement enregistré', `${totalPaidByProduct.toFixed(2)}€ encaissés par produit`);
        onClose();
        onSuccess?.();
      } catch (error) {
        console.error('Payment error:', error);
        toast.error('Erreur paiement', 'Impossible d\'enregistrer le paiement');
      } finally {
        setLoading(false);
      }
      return;
    }

    // Mode paiement total (original)
    if (payments.length === 0) {
      toast.warning('Aucun paiement', 'Ajoutez au moins un paiement');
      return;
    }

    if (remaining > 0.01) { // Tolérance de 1 centime
      toast.warning('Paiement incomplet', `Il reste ${remaining.toFixed(2)}€ à payer`);
      return;
    }

    setLoading(true);

    try {
      // Si un seul paiement, utiliser l'ancien format
      // Si multi-paiements, envoyer le tableau
      // IMPORTANT: Utiliser changeToGive (monnaie calculée) et non totalPaid - total
      // SÉCURITÉ: change_given ne peut pas être négatif
      const safeChangeToGive = Math.max(0, changeToGive);
      const paymentData = payments.length === 1 ? {
        payment_method: payments[0].method,
        amount_received: payments[0].amountGiven || payments[0].amount,
        change_given: safeChangeToGive
      } : {
        payment_method: 'multi',
        payments: payments,
        amount_received: payments.reduce((sum, p) => sum + (p.amountGiven || p.amount), 0),
        change_given: safeChangeToGive
      };

      // FIREBASE DIRECT (plus de backend Python)
      await ordersService.updatePayment(order.id, paymentData);

      toast.success('Paiement enregistré', `${totalPaid.toFixed(2)}€ encaissés`);
      onClose();
      onSuccess?.();
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Erreur paiement', 'Impossible d\'enregistrer le paiement');
    } finally {
      setLoading(false);
    }
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      'espece': '💵 Espèce',
      'cb': '💳 CB',
      'cheque': '📝 Chèque',
      'ticket_restaurant': '🎟️ Ticket restaurant'
    };
    return labels[method] || method;
  };

  // Calcul du rendu de monnaie pour un produit
  const calculateProductChange = (itemIndex, itemPrice) => {
    const received = amountReceivedByProduct[itemIndex];
    if (received === undefined || received === '' || received === null) return null;

    const receivedNum = parseFloat(received) || 0;
    const change = receivedNum - itemPrice;

    if (change > 0) {
      return <span style={{ color: '#16a34a', fontWeight: 'bold', fontSize: '12px' }}>Rendre: {change.toFixed(2)}€</span>;
    } else if (change === 0) {
      return <span style={{ color: '#16a34a', fontSize: '12px' }}>✓ Exact</span>;
    } else {
      return <span style={{ color: '#dc2626', fontSize: '12px' }}>Manque: {Math.abs(change).toFixed(2)}€</span>;
    }
  };

  // Calcul du rendu de monnaie pour le total
  const calculateTotalChange = () => {
    if (amountReceivedTotal === '' || amountReceivedTotal === null) return null;

    const receivedNum = parseFloat(amountReceivedTotal) || 0;
    const orderTotal = parseFloat(order?.total) || 0;
    const change = receivedNum - orderTotal;

    if (change > 0) {
      return <span style={{ color: '#16a34a', fontWeight: 'bold' }}>💰 Rendre: {change.toFixed(2)}€</span>;
    } else if (change === 0) {
      return <span style={{ color: '#16a34a' }}>✓ Compte exact</span>;
    } else {
      return <span style={{ color: '#dc2626' }}>Manque: {Math.abs(change).toFixed(2)}€</span>;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="medium">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold">
          {isModifyingPayment ? '✏️ Modifier le paiement' : '💳 Enregistrer le paiement'}
        </h2>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Avertissement paiement verrouillé */}
      {isPaymentLocked && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2">
            <span className="text-xl">🔒</span>
            <p className="text-sm text-red-700 font-medium">
              Paiement en ligne non modifiable
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Sélecteur de mode de paiement - FIX Build 201: Afficher dès qu'il y a des articles */}
        {!isPaymentLocked && !isModifyingPayment && order?.items?.length >= 1 && (
          <div className="flex gap-2 p-2 bg-gray-100 rounded-lg">
            <button
              type="button"
              onClick={() => setPaymentMode('total')}
              className={`flex-1 py-2 px-4 rounded-lg font-bold transition-all ${
                paymentMode === 'total'
                  ? 'bg-white shadow-md text-blue-600'
                  : 'text-gray-500 hover:bg-gray-200'
              }`}
            >
              💳 Paiement Total
            </button>
            <button
              type="button"
              onClick={() => setPaymentMode('product')}
              className={`flex-1 py-2 px-4 rounded-lg font-bold transition-all ${
                paymentMode === 'product'
                  ? 'bg-white shadow-md text-purple-600'
                  : 'text-gray-500 hover:bg-gray-200'
              }`}
            >
              🛒 Par Produit
            </button>
          </div>
        )}

        {/* Order Summary avec RESTANT À PAYER */}
        <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border-2 border-blue-200">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <span className="text-sm text-gray-600">Commande:</span>
              <span className="ml-2 font-bold">#{order?.id?.slice(0, 8)}</span>
            </div>
            <div className="text-right">
              <span className="text-sm text-gray-600">Total:</span>
              <span className="ml-2 font-bold text-lg">{order?.total?.toFixed(2)}€</span>
            </div>
            <div>
              <span className="text-sm text-gray-600">Déjà payé:</span>
              <span className="ml-2 font-bold text-green-600">{totalPaid.toFixed(2)}€</span>
            </div>
            <div className="text-right">
              <span className="text-sm text-gray-600">Restant:</span>
              <span className={`ml-2 font-bold text-xl ${remaining > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {remaining.toFixed(2)}€
              </span>
            </div>
          </div>

          {/* Calcul rendu monnaie pour le total (mode paiement total) */}
          {paymentMode === 'total' && !isPaymentLocked && (
            <div className="mt-3 pt-3 border-t border-blue-200 flex items-center gap-3">
              <span className="text-sm text-gray-600">💵 Reçu du client:</span>
              <input
                type="number"
                step="0.01"
                placeholder={`${order?.total?.toFixed(2)}€`}
                style={{ width: 90, padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: '14px' }}
                value={amountReceivedTotal}
                onChange={(e) => setAmountReceivedTotal(e.target.value)}
              />
              {calculateTotalChange()}
            </div>
          )}
        </div>

        {/* Liste des paiements enregistrés */}
        {payments.length > 0 && (
          <div className="space-y-2">
            <h3 className="font-semibold text-sm text-gray-700">Paiements enregistrés:</h3>
            {payments.map((payment, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <span className="font-medium">{getPaymentMethodLabel(payment.method)}</span>
                    <span className="text-gray-400">→</span>
                    <span className="font-bold text-green-600">{payment.amount.toFixed(2)}€</span>
                  </div>
                  {payment.change > 0 && (
                    <div className="text-sm text-orange-600 mt-1">
                      💵 Reçu {payment.amountGiven?.toFixed(2)}€ → 💰 Monnaie: {payment.change.toFixed(2)}€
                    </div>
                  )}
                </div>
                {!isPaymentLocked && (
                  <button
                    type="button"
                    onClick={() => handleRemovePayment(index)}
                    className="text-red-500 hover:text-red-700 ml-2"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Monnaie totale à rendre */}
        {changeToGive > 0 && paymentMode === 'total' && (
          <div className="p-4 bg-gradient-to-r from-orange-100 to-yellow-100 border-2 border-orange-300 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-orange-800 font-bold text-lg">💰 Monnaie à rendre</span>
              <span className="text-2xl font-black text-orange-600">{changeToGive.toFixed(2)}€</span>
            </div>
          </div>
        )}

        {/* MODE PAIEMENT PAR PRODUIT */}
        {paymentMode === 'product' && !isPaymentLocked && (
          <div className="border-2 border-purple-300 rounded-lg p-4 bg-purple-50">
            <h3 className="font-bold text-purple-800 mb-3">🛒 Payer chaque produit</h3>

            {/* Résumé */}
            <div className="mb-4 p-3 bg-white rounded-lg">
              <div className="flex justify-between text-sm">
                <span>Payé:</span>
                <span className="font-bold text-green-600">{totalPaidByProduct.toFixed(2)}€</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Restant:</span>
                <span className={`font-bold ${remainingByProduct > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {remainingByProduct.toFixed(2)}€
                </span>
              </div>
            </div>

            {/* Liste des produits */}
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {order?.items?.map((item, index) => {
                const itemTotal = (item.unit_price || item.price || 0) * (item.quantity || 1);

                // Ignorer les produits offerts (0€)
                if (itemTotal <= 0) return null;

                const payment = productPayments[index];

                return (
                  <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                    <div className="flex-1">
                      <p className="font-medium text-sm">{item.quantity}x {item.name}</p>
                      <p className="text-gray-500 text-xs">{itemTotal.toFixed(2)}€</p>
                    </div>

                    {/* CAS 1: Paiement COMPLET (remaining === 0) */}
                    {payment && payment.remaining === 0 ? (
                      <div className="flex items-center gap-2">
                        {/* Afficher tous les paiements */}
                        <div className="flex flex-wrap gap-1">
                          {payment.payments?.map((p, pIndex) => (
                            <span key={pIndex} className="text-xs font-bold text-green-600 bg-green-100 px-2 py-1 rounded">
                              ✓ {p.amount.toFixed(2)}€ {p.method === 'espece' ? 'ESP' : p.method === 'cb' ? 'CB' : 'TR'}
                            </span>
                          )) || (
                            <span className="text-xs font-bold text-green-600 bg-green-100 px-2 py-1 rounded">
                              ✓ {getPaymentMethodLabel(payment.method)}
                            </span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveProductPayment(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : payment && payment.remaining > 0 ? (
                      /* CAS 2: Paiement PARTIEL (remaining > 0) */
                      <div className="flex flex-col gap-2">
                        {/* Paiements déjà effectués */}
                        <div className="flex flex-wrap gap-1">
                          {payment.payments?.map((p, pIndex) => (
                            <span key={pIndex} className="text-xs font-bold text-green-600 bg-green-100 px-2 py-1 rounded">
                              ✓ {p.amount.toFixed(2)}€ {p.method === 'espece' ? 'ESP' : p.method === 'cb' ? 'CB' : 'TR'}
                            </span>
                          ))}
                        </div>
                        {/* Reste à payer */}
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-orange-600">
                            Reste: {payment.remaining.toFixed(2)}€
                          </span>
                          <input
                            type="number"
                            step="0.01"
                            placeholder="Reçu"
                            style={{ width: 55, padding: '3px 5px', border: '1px solid #ddd', borderRadius: 4, fontSize: '11px' }}
                            value={amountReceivedByProduct[index] || ''}
                            onChange={(e) => setAmountReceivedByProduct(prev => ({
                              ...prev,
                              [index]: e.target.value
                            }))}
                          />
                          <button type="button" onClick={() => handlePayProduct(index, 'espece')}
                            className="px-2 py-1 text-xs font-bold bg-green-500 text-white rounded hover:bg-green-600">
                            ESP
                          </button>
                          <button type="button" onClick={() => handlePayProduct(index, 'cb')}
                            className="px-2 py-1 text-xs font-bold bg-blue-500 text-white rounded hover:bg-blue-600">
                            CB
                          </button>
                          <button type="button" onClick={() => handlePayProduct(index, 'ticket_restaurant')}
                            className="px-2 py-1 text-xs font-bold bg-orange-500 text-white rounded hover:bg-orange-600">
                            TR
                          </button>
                          <button type="button" onClick={() => handleRemoveProductPayment(index)}
                            className="text-red-500 hover:text-red-700">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* CAS 3: Pas encore de paiement */
                      <div className="flex items-center gap-2">
                        {/* Champ montant reçu + calcul rendu */}
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            step="0.01"
                            placeholder="Reçu"
                            style={{ width: 65, padding: '4px 6px', border: '1px solid #ddd', borderRadius: 4, fontSize: '12px' }}
                            value={amountReceivedByProduct[index] || ''}
                            onChange={(e) => setAmountReceivedByProduct(prev => ({
                              ...prev,
                              [index]: e.target.value
                            }))}
                          />
                          {calculateProductChange(index, itemTotal)}
                        </div>
                        {/* Boutons de paiement */}
                        <button
                          type="button"
                          onClick={() => handlePayProduct(index, 'espece')}
                          className="px-3 py-2 text-xs font-bold bg-green-500 text-white rounded-lg hover:bg-green-600 shadow-md transition-all hover:scale-105"
                        >
                          💵 ESP
                        </button>
                        <button
                          type="button"
                          onClick={() => handlePayProduct(index, 'cb')}
                          className="px-3 py-2 text-xs font-bold bg-blue-500 text-white rounded-lg hover:bg-blue-600 shadow-md transition-all hover:scale-105"
                        >
                          💳 CB
                        </button>
                        <button
                          type="button"
                          onClick={() => handlePayProduct(index, 'ticket_restaurant')}
                          className="px-3 py-2 text-xs font-bold bg-orange-500 text-white rounded-lg hover:bg-orange-600 shadow-md transition-all hover:scale-105"
                        >
                          🎟️ TR
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Boutons rapides */}
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={() => {
                  const newPayments = {};
                  order?.items?.forEach((item, index) => {
                    const itemTotal = (item.unit_price || item.price || 0) * (item.quantity || 1);
                    newPayments[index] = {
                      itemName: item.name,
                      itemTotal,
                      payments: [{ method: 'espece', amount: itemTotal }],
                      totalPaid: itemTotal,
                      remaining: 0
                    };
                  });
                  setProductPayments(newPayments);
                  setAmountReceivedByProduct({});
                }}
                className="flex-1 py-2 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600"
              >
                💵 Tout en espèces
              </button>
              <button
                type="button"
                onClick={() => {
                  const newPayments = {};
                  order?.items?.forEach((item, index) => {
                    const itemTotal = (item.unit_price || item.price || 0) * (item.quantity || 1);
                    newPayments[index] = {
                      itemName: item.name,
                      itemTotal,
                      payments: [{ method: 'cb', amount: itemTotal }],
                      totalPaid: itemTotal,
                      remaining: 0
                    };
                  });
                  setProductPayments(newPayments);
                  setAmountReceivedByProduct({});
                }}
                className="flex-1 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                💳 Tout en CB
              </button>
            </div>
          </div>
        )}

        {/* Ajouter un paiement (mode total uniquement) */}
        {!isPaymentLocked && remaining > 0.01 && paymentMode === 'total' && (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 space-y-3">
            <h3 className="font-semibold text-sm text-gray-700">Ajouter un paiement:</h3>
            
            {/* Modes de paiement */}
            <div>
              <label className="block text-sm font-medium mb-2">Mode de paiement *</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setCurrentPaymentMethod('espece')}
                  className={`p-3 rounded-lg border-2 transition-all text-sm font-bold ${
                    currentPaymentMethod === 'espece'
                      ? 'border-green-600 bg-green-500 text-white shadow-lg scale-105'
                      : 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100'
                  }`}
                >
                  💵 Espèce
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPaymentMethod('cb')}
                  className={`p-3 rounded-lg border-2 transition-all text-sm font-bold ${
                    currentPaymentMethod === 'cb'
                      ? 'border-blue-600 bg-blue-500 text-white shadow-lg scale-105'
                      : 'border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100'
                  }`}
                >
                  💳 CB
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPaymentMethod('cheque')}
                  className={`p-3 rounded-lg border-2 transition-all text-sm font-bold ${
                    currentPaymentMethod === 'cheque'
                      ? 'border-purple-600 bg-purple-500 text-white shadow-lg scale-105'
                      : 'border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100'
                  }`}
                >
                  📝 Chèque
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPaymentMethod('ticket_restaurant')}
                  className={`p-3 rounded-lg border-2 transition-all text-sm font-bold ${
                    currentPaymentMethod === 'ticket_restaurant'
                      ? 'border-orange-600 bg-orange-500 text-white shadow-lg scale-105'
                      : 'border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-100'
                  }`}
                >
                  🎟️ Ticket restaurant
                </button>
              </div>
            </div>

            {/* Montant */}
            <div>
              <label className="block text-sm font-medium mb-2">Montant</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  step="0.01"
                  value={currentAmount}
                  onChange={(e) => setCurrentAmount(e.target.value)}
                  placeholder={`Restant: ${remaining.toFixed(2)}€`}
                  className="flex-1 px-3 py-2 border rounded-lg"
                />
                <Button
                  type="button"
                  onClick={() => setCurrentAmount(remaining.toFixed(2))}
                  className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white hover:from-indigo-600 hover:to-purple-600 font-bold"
                >
                  Tout le restant
                </Button>
              </div>
            </div>

            <Button
              type="button"
              onClick={handleAddPayment}
              className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:from-green-600 hover:to-emerald-600 font-bold shadow-lg"
            >
              <Plus className="w-4 h-4 mr-2" />
              Ajouter ce paiement
            </Button>
          </div>
        )}

        {/* Boutons d'action */}
        <div className="flex gap-2 pt-4 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="flex-1"
            disabled={loading}
          >
            Annuler
          </Button>
          <Button
            type="submit"
            className={`flex-1 font-bold text-white shadow-lg ${
              (paymentMode === 'total' ? remaining : remainingByProduct) > 0.01
                ? 'bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600'
                : 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700'
            }`}
            disabled={loading || isPaymentLocked || (paymentMode === 'total' ? payments.length === 0 : Object.keys(productPayments).length === 0)}
          >
            {loading ? 'Enregistrement...' :
              paymentMode === 'product' ?
                (remainingByProduct > 0.01 ? `⚠️ Il reste ${remainingByProduct.toFixed(2)}€` : '✅ Valider paiement par produit') :
                (remaining > 0.01 ? `⚠️ Il reste ${remaining.toFixed(2)}€` : '✅ Valider le paiement complet')
            }
          </Button>
        </div>
      </form>
    </Modal>
  );
};
